import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import BoardFilterChips from './board-filter-chips';
import ViewSwitch from './view-switch';
import Button from '../../../common/components/button/button';
import FilterChips from '../../../common/components/filter-chips/filter-chips';
import ListRow from '../../../common/components/list-row/list-row';
import IconButton from '../../../common/components/icon-button/icon-button';
import PickerSheet from '../../../common/components/picker-sheet/picker-sheet';
import RelativeTime from '../../../common/components/relative-time/relative-time';
import StatusPill from '../../../common/components/status-pill/status-pill';
import EmptyState from '../../../common/components/state-message/empty-state';
import ErrorState from '../../../common/components/state-message/error-state';
import LoadingState from '../../../common/components/state-message/loading-state';
import FolderIcon from '../../../common/icons/folder';
import { ESTIMATE_LABEL, PROJECT_STATE_LABEL, TASK_KIND_LABEL, WORKFLOW_STATE_LABEL, WORKFLOW_STATE_TONE } from '../../../core/constants/domain';
import { ROUTES } from '../../../core/constants/routes';
import { nameOf, useProjectCatalog, useTierCatalog } from '../../../global/contexts/catalogs/catalogs';
import { useTasksList } from '../../../global/contexts/work/work';
import { hasActiveFilters, KIND_FILTERS, readFilters, STATE_FILTERS } from '../utils/filters';

import '../styles/queue.css';

export default function WorkQueue() {
    const [params, setParams] = useSearchParams();
    const filters = readFilters(params);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const { data: projects } = useProjectCatalog();
    const { data: tiers } = useTierCatalog();
    const { data, isPending, isError, error, refetch, isPlaceholderData } = useTasksList({
        workflowState: filters.state,
        kind: filters.kind,
        projectId: filters.projectId,
    });

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, String(value));
        else next.delete(key);
        setParams(next, { replace: true });
    };

    const tasks = data?.items ?? [];

    return (
        <div className="work-queue">
            <header className="work-queue__header">
                <h1 className="work-queue__title">Cola de tareas</h1>
                <div className="work-queue__tools">
                    <IconButton
                        icon={<FolderIcon size={20} />}
                        label={filters.projectId ? `Proyecto: ${nameOf(projects, filters.projectId, 'Proyecto')}. Cambiar` : 'Elegir proyecto'}
                        badge={filters.projectId ? true : undefined}
                        isActive={!!filters.projectId}
                        aria-haspopup="dialog"
                        onClick={() => setIsPickerOpen(true)}
                    />
                    <ViewSwitch current="lista" />
                </div>
            </header>
            <BoardFilterChips filters={{ projectId: filters.projectId }} projects={projects} onChange={setParam} />

            <div className="work-queue__filters">
                <FilterChips label="Estado" options={STATE_FILTERS} value={filters.state} onChange={(value) => setParam('state', value)} />
                <FilterChips label="Tipo" options={KIND_FILTERS} value={filters.kind} onChange={(value) => setParam('kind', value)} />
            </div>

            <PickerSheet
                isOpen={isPickerOpen}
                title="Proyecto"
                options={[
                    { value: '', label: 'Todos los proyectos', icon: <FolderIcon size={18} /> },
                    ...[...(projects?.values() ?? [])].map((project) => ({
                        value: project.id,
                        label: project.name,
                        description: PROJECT_STATE_LABEL[project.state] ?? undefined,
                        icon: <FolderIcon size={18} />,
                    })),
                ]}
                value={filters.projectId ?? ''}
                onSelect={(value) => setParam('project', value)}
                onClose={() => setIsPickerOpen(false)}
            />

            <p className="work-queue__count" aria-live="polite">
                {isPending ? 'Cargando…' : `${data?.total ?? 0} tareas`}
                {data?.hasMore && ` · se muestran las primeras ${tasks.length}`}
            </p>

            {isPending && <LoadingState label="Cargando tareas" rows={5} />}
            {isError && <ErrorState error={error} onRetry={refetch} />}
            {!isPending && !isError && tasks.length === 0 && (
                <EmptyState
                    title="No hay tareas con estos filtros"
                    description="Cambia el estado o el proyecto para ver más trabajo."
                    action={hasActiveFilters(filters)
                        ? <Button size="small" onClick={() => setParams({ vista: 'lista' }, { replace: true })}>Quitar filtros</Button>
                        : null}
                />
            )}
            {tasks.length > 0 && (
                <ul className={`work-queue__list${isPlaceholderData ? ' work-queue__list--refreshing' : ''}`}>
                    {tasks.map((task) => (
                        <ListRow
                            key={task.id}
                            to={ROUTES.TASK(task.id)}
                            tone={WORKFLOW_STATE_TONE[task.state]}
                            eyebrow={`#${task.id} · ${nameOf(projects, task.projectId, 'Proyecto')} · ${nameOf(tiers, task.tierId, 'Tier')}`}
                            title={task.title}
                            meta={(
                                <>
                                    <StatusPill tone={WORKFLOW_STATE_TONE[task.state]}>
                                        {WORKFLOW_STATE_LABEL[task.state] ?? task.state}
                                    </StatusPill>
                                    <span>{TASK_KIND_LABEL[task.kind] ?? task.kind}</span>
                                    {task.estimate && <span>{ESTIMATE_LABEL[task.estimate] ?? task.estimate}</span>}
                                    <span>{task.assignee ?? 'Sin responsable'}</span>
                                    <span>Actualizada <RelativeTime value={task.updatedAt} /></span>
                                </>
                            )}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}
